import React, {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
} from 'react';

import reducer, {initialState} from './reducer';
import actions from './actions';
import {ACTIONS} from './constants';
import type {FixedSizeList, VariableSizeList} from 'react-window';

import useUpdateEffect from '@netx/core-components/hooks/useUpdateEffect';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import useDocumentKeyDown from '@netx/core-components/hooks/useDocumentKeyDown';

export interface UseVirtualizedListArrowTabTrapParams {
	activeDescendantLabel?: string;
	addedAtIndex?: number;
	pageSize?: number;
	virtualListRef: React.RefObject<FixedSizeList | VariableSizeList | null>;
	total: number;
	align?: 'auto' | 'smart' | 'center' | 'end' | 'start';
	disableScrollToOnFocusCapture?: boolean;

	onItemKeyDown?: (
		event: KeyboardEvent,
		rowIndex: number,
		click: boolean,
	) => void;
}

const FOCUS_TRAP_NAVIGATION_KEYS = [
	'ArrowDown',
	'ArrowUp',
	'End',
	'Home',
	'PageDown',
	'PageUp',
] as const;

const FOCUS_TRAP_NAVIGATION = {
	ArrowDown: 'nextItem',
	ArrowUp: 'prevItem',
	End: 'end',
	Home: 'home',
	PageDown: 'nextPage',
	PageUp: 'prevPage',
} as const;

export const useVirtualizedListArrowTabTrap = ({
	activeDescendantLabel = 'row',
	addedAtIndex = -1,
	pageSize = 25,
	total,
	virtualListRef,
	align,
	disableScrollToOnFocusCapture,
	onItemKeyDown,
}: UseVirtualizedListArrowTabTrapParams) => {
	const [state, dispatch] = useReducer(reducer, {
		...initialState,
		pageSize,
		total,
	});

	const rootRef = useRef<HTMLDivElement>(null);

	const activeIndexRef = useRef(state.activeIndex);
	activeIndexRef.current = state.activeIndex;

	const disabledRef = useRef(state.disabled);
	disabledRef.current = state.disabled;

	const activeRef = useRef(state.active);
	activeRef.current = state.active;

	const interactingWithItemRef = useRef(state.interactingWithItem);
	interactingWithItemRef.current = state.interactingWithItem;

	const focusedOnceRef = useRef(false);

	const disableNextFocusCapture = useRef(false);
	const disableNextScrollUpdate = useRef(false);
	const disableNextIndexUpdate = useRef(false);

	// The reducer actions to expose to outside components
	const dispatchActions = useMemo(() => actions(dispatch), []);

	const activeDescendant =
		!state.disabled && state.activeIndex > -1
			? `${activeDescendantLabel}-${state.activeIndex.toString()}`
			: undefined;

	// TODO: we need to make this support "classic paging" - utlize page/pageSize/chunkSize
	// when available
	useUpdateEffect(() => {
		dispatch({type: ACTIONS.SET_PAGE_SIZE, pageSize});
	}, [pageSize]);

	useUpdateEffect(() => {
		dispatch({type: ACTIONS.SET_TOTAL, total});
	}, [total]);

	useEffect(() => {
		if (!state.active) return;
		focusedOnceRef.current = true;
	}, [state.active]);

	useEffect(() => {
		if (
			disabledRef.current ||
			!virtualListRef?.current ||
			(virtualListRef.current.state as any).isScrolling
		)
			return;
		virtualListRef.current.scrollToItem(state.activeIndex, align);
	}, [state.activeIndex]);

	// Fix for table rows
	useWhenChangedEffect(
		[state.activeIndex, total] as const,
		useCallback(([activeIndex, total]) => {
			const shouldFocus = !disableNextIndexUpdate.current;
			disableNextIndexUpdate.current = false;
			if (
				!activeRef.current ||
				disabledRef.current ||
				!rootRef.current ||
				activeIndex < 0
			)
				return;

			const row = rootRef.current.querySelector(
				`[aria-rowindex="${activeIndex}"]`,
			);
			// force blur on any active elements
			// testing for and using `document.activeElement.focus()` does not
			// acheive what we want (and may be a shortcoming of some deeper logic
			// in the "focus trap")
			if (row && shouldFocus) {
				(row as HTMLElement).click();
				rootRef.current?.focus({
					preventScroll: true,
				});
			}
		}, []),
	);

	useDocumentKeyDown(
		useCallback(
			(event) => {
				if (activeIndexRef.current > -1) {
					onItemKeyDown?.(
						event,
						activeIndexRef.current,
						[' ', 'Enter'].includes(event.key as any),
					);
					if (event.defaultPrevented) return;
				}

				if (!FOCUS_TRAP_NAVIGATION_KEYS.includes(event.key as any)) return;

				disableNextScrollUpdate.current = true;
				// @ts-ignore
				dispatchActions[FOCUS_TRAP_NAVIGATION[event.key]]();

				return false;
			},
			[onItemKeyDown],
		),
		{
			disabled: state.disabled || !state.active || state.interactingWithItem,
		},
	);

	const onBlur = useCallback((event: React.FocusEvent) => {
		if (event.currentTarget.contains(event.relatedTarget as Node)) return;
		dispatchActions.setActive(false);
	}, []);

	const onFocusCapture = useCallback(
		(event: React.FocusEvent) => {
			dispatchActions.setActive(true);

			if (disableNextFocusCapture.current) {
				disableNextFocusCapture.current = false;
				return;
			}

			if (
				disabledRef.current ||
				disableScrollToOnFocusCapture ||
				!virtualListRef.current
			)
				return;

			if (
				!(virtualListRef.current.state as any).isScrolling &&
				!activeRef.current &&
				event.target === rootRef.current
			) {
				virtualListRef.current.scrollToItem(activeIndexRef.current);
			}
		},
		[disableScrollToOnFocusCapture],
	);

	const onItemClick = useCallback((index: number) => {
		dispatchActions.setActive(true);
		if (activeIndexRef.current !== index) {
			dispatchActions.setActiveIndex(index);
		}
		rootRef.current?.focus({
			preventScroll: true,
		});
	}, []);

	const onItemInteracting = useCallback(
		(index: number, interacting: boolean) => {
			dispatchActions.setInteractingWithItem(interacting);
			// Get "focus" back on the `activeIndex`
			if (activeIndexRef.current === index && !interacting) {
				onItemClick(index);
			}
		},
		[onItemClick],
	);

	useWhenChangedEffect(
		addedAtIndex,
		useCallback(
			(addedAtIndex) => {
				if (addedAtIndex < 0) return;
				disableNextFocusCapture.current = true;
				disableNextScrollUpdate.current = true;
				onItemClick(addedAtIndex);
			},
			[onItemClick],
		),
	);

	const onScrollIndexUpdate = useCallback(
		(index: number, page: number, visibleStopIndex: number) => {
			if (!disableNextScrollUpdate.current || !activeRef.current) {
				disableNextIndexUpdate.current = !activeRef.current;
				dispatchActions.setActiveIndex(index);
				dispatchActions.setInteractingWithItem(false);
			}
			disableNextScrollUpdate.current = false;
		},
		[],
	);

	return {
		state,
		actions: dispatchActions,
		virtualListRef,

		activeDescendant,
		onBlur,
		onFocusCapture,
		onItemClick,
		onItemInteracting,
		onScrollIndexUpdate,
		rootRef,
	};
};

export default useVirtualizedListArrowTabTrap;
